import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Stock } from '../types';
import { isStockFetchError } from '../utils/errors';

export const stockListQueryKeys = {
  all: ['stockList'] as const,
  list: (options?: StockListOptions) => [...stockListQueryKeys.all, 'list', options] as const,
};

interface StockListOptions {
  forceRefresh?: boolean;
}

interface StockListData {
  stocks: Stock[];
  totalCount?: number;
  isComplete: boolean;
  lastUpdated: number;
}

export const useStockList = (options: StockListOptions = {}) => {
  const queryClient = useQueryClient();

  console.log('Hook called with options:', options);

  const existingCache = queryClient.getQueryData<StockListData>(stockListQueryKeys.list(options));

  if (existingCache) {
    const age = Date.now() - existingCache.lastUpdated;
    console.log('Found existing cache:', {
      stocks: existingCache.stocks.length,
      age: `${Math.round(age / 1000)}s`,
      isComplete: existingCache.isComplete,
      shouldUseCache: age < 10 * 60 * 1000 && existingCache.isComplete && !options.forceRefresh,
    });
  } else {
    console.log('No existing cache found');
  }

  const query = useQuery({
    queryKey: stockListQueryKeys.list(options),

    queryFn: async (): Promise<StockListData> => {
      console.log('QueryFn EXECUTED - API calls will start!');

      const existingData = queryClient.getQueryData<StockListData>(
        stockListQueryKeys.list(options)
      );

      if (existingData && !options.forceRefresh) {
        const age = Date.now() - existingData.lastUpdated;
        const staleTime = 10 * 60 * 1000;

        if (age < staleTime && existingData.isComplete) {
          console.log(
            `Using cached data (age: ${Math.round(age / 1000)}s, ${
              existingData.stocks.length
            } stocks)`
          );
          return existingData;
        } else {
          console.log(
            `Cache exists but stale (age: ${Math.round(age / 1000)}s) or incomplete (${
              existingData.isComplete
            }), fetching fresh data`
          );
        }
      } else {
        console.log('No cache or forceRefresh=true, fetching fresh data');
      }

      const { fetchAllStocks } = await import('../services/stocksApi');

      try {
        const stocks = await fetchAllStocks({
          ...(options.forceRefresh !== undefined && {
            forceRefresh: options.forceRefresh,
          }),
          onProgress: (loaded, total) => {
            console.log(`Progress update: ${loaded}/${total}`);
          },
          onStocksUpdate: progressiveStocks => {
            console.log(`Updating cache with ${progressiveStocks.length} stocks`);

            queryClient.setQueryData(
              stockListQueryKeys.list(options),
              (old: StockListData | undefined): StockListData => {
                const updated = {
                  stocks: progressiveStocks,
                  totalCount: progressiveStocks.length,
                  isComplete: false,
                  lastUpdated: Date.now(),
                };

                console.log(
                  `Cache updated: ${updated.stocks.length} stocks (was: ${old?.stocks.length || 0})`
                );

                return updated;
              }
            );
          },
        });

        const result = {
          stocks,
          totalCount: stocks.length,
          isComplete: true,
          lastUpdated: Date.now(),
        };

        console.log('Query function returning:', {
          stocksLength: result.stocks.length,
          totalCount: result.totalCount,
          isComplete: result.isComplete,
          firstStock: result.stocks[0]?.symbol || 'none',
        });

        return result;
      } catch (error) {
        if (isStockFetchError(error)) {
          if (error.isPartialFailure) {
            const existingData = queryClient.getQueryData<StockListData>(
              stockListQueryKeys.list(options)
            );

            if (existingData?.stocks && existingData.stocks.length > 0) {
              console.warn('Using cached data due to partial failure:', error.message);
              return existingData;
            }
          }
        }

        throw error;
      }
    },

    retry: (failureCount, error) => {
      if (isStockFetchError(error)) {
        switch (error.errorType) {
          case 'api_error':
            return failureCount < 2;
          case 'rate_limit':
            return failureCount < 5;
          case 'network':
            return failureCount < 8;
          default:
            return failureCount < 3;
        }
      }
      return failureCount < 3;
    },

    retryDelay: (attemptIndex, error) => {
      if (isStockFetchError(error) && error.errorType === 'rate_limit') {
        return 30000;
      }
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    },

    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    refetchInterval: false,
    refetchIntervalInBackground: false,

    placeholderData: previousData => previousData,
  });

  const refetch = () => query.refetch();

  const clearCache = () => {
    queryClient.removeQueries({ queryKey: stockListQueryKeys.all });
  };

  const getErrorInfo = () => {
    if (!query.error) return null;

    if (isStockFetchError(query.error)) {
      return {
        type: query.error.errorType,
        isPartial: query.error.isPartialFailure,
        loaded: query.error.stocksLoaded,
        total: query.error.totalAttempted,
        canRetry: query.error.errorType !== 'api_error',
      };
    }

    return {
      type: 'unknown' as const,
      isPartial: false,
      loaded: 0,
      total: 0,
      canRetry: true,
    };
  };

  if (import.meta.env.DEV) {
    const currentStocks = query.data?.stocks || [];
    const currentState = {
      isLoading: query.isLoading,
      isError: query.isError,
      isRefetching: query.isRefetching,
      stocksCount: currentStocks.length,
      hasError: !!query.error,
      errorMessage: query.error?.message,
      queryStatus: query.status,
      fetchStatus: query.fetchStatus,
      timestamp: new Date().toISOString(),
    };

    console.log('Current state:', currentState);
    if (query.status === 'success' && currentStocks.length > 0) {
      console.log('SUCCESS: Stocks loaded successfully:', currentStocks.length);
    }
    if (query.status === 'error') {
      console.log('ERROR:', query.error?.message);
    }
    if (query.isRefetching) {
      console.log('REFETCHING: Background update in progress');
    }
  }

  const hasStocks = (query.data?.stocks?.length || 0) > 0;

  const returnData = {
    stocks: query.data?.stocks || [],
    totalCount: query.data?.totalCount,
    isComplete: query.data?.isComplete || false,
    lastUpdated: query.data?.lastUpdated,

    isLoading: query.isLoading && !hasStocks,
    isError: query.isError && !hasStocks,
    error: query.error && !hasStocks ? query.error : null,
    isRefetching: query.isRefetching,
    isStale: query.isStale,

    isLoadingMore: query.isLoading && hasStocks,
    hasPartialData: hasStocks && (query.isLoading || query.isRefetching),

    // Error info
    errorInfo: getErrorInfo(),

    // Actions
    refetch,
    clearCache,

    // Raw React Query object for advanced usage
    query,
  };

  if (import.meta.env.DEV) {
    console.log('Returning data:', {
      stocksLength: returnData.stocks.length,
      isLoading: returnData.isLoading,
      isError: returnData.isError,
      queryStatus: query.status,
      queryFetchStatus: query.fetchStatus,
      hasData: !!query.data,
      dataStocks: query.data?.stocks?.length || 0,
    });

    if (returnData.stocks.length > 0 && query.status === 'success') {
      console.log('DATA UPDATE: Context will receive', returnData.stocks.length, 'stocks!');
    }
  }

  return returnData;
};

export const useStockListCache = () => {
  const queryClient = useQueryClient();

  return {
    getCachedStocks: () => {
      const data = queryClient.getQueryData<StockListData>(stockListQueryKeys.list());
      return data?.stocks || [];
    },

    getCacheInfo: () => {
      const data = queryClient.getQueryData<StockListData>(stockListQueryKeys.list());
      return {
        exists: !!data,
        count: data?.stocks.length || 0,
        isComplete: data?.isComplete || false,
        lastUpdated: data?.lastUpdated,
        age: data?.lastUpdated ? Date.now() - data.lastUpdated : 0,
      };
    },

    invalidateCache: () => {
      queryClient.invalidateQueries({ queryKey: stockListQueryKeys.all });
    },
  };
};
